import React from "react";
import { useAppSelector } from "../../../app/hooks";
import { selectLabels } from "../../../slices/language";
import { TextField } from "@mui/material";
import { AccessGroupsData } from "../../../api/models/access-groups";
import RequirementsComponent from "../../RequirementsComponent";
import IconifyIcon from "../../IconifyIcon";
import {
  formatAccessScheduleLabel,
  formatAccessScheduleTime,
  formatAllowedDaysLabel,
  getAccessPhotoIcon,
  getAccessPhotoLabel,
  getExitRestrictionFrom,
  getExitRestrictionTo,
  hasExitRestrictionSchedule,
} from "../../../utils/accessGroupScheduleDisplay";

interface NewInvitationVisitDetailsProps {
  motive: string;
  setMotive: (motive: string) => void;
  identifier: string;
  setIdentifier: React.Dispatch<React.SetStateAction<string>>;
  multipleAccess: boolean;
  antipassback: boolean;
  setAntipassback: (antipassback: boolean) => void;
  selectedAccessPoints: string[];
  setSelectedAccessPoints: (selectedAccessPoints: string[]) => void;
  accessGroups: AccessGroupsData[];
  requirementValues: any;
  setRequirementValues: (requirementValues: any) => void;
  isMobile: boolean;
  invUsers?: string;
  setInvUsers?: React.Dispatch<React.SetStateAction<string>>;
  selectedAccessGroups: AccessGroupsData[] | null;
  setSelectedAccessGroups: (
    selectedAccessGroup: AccessGroupsData[] | null,
  ) => void;
}

const NewInvitationVisitDetails = ({
  motive,
  setMotive,
  multipleAccess,
  antipassback,
  setAntipassback,
  selectedAccessPoints,
  setSelectedAccessPoints,
  identifier,
  setIdentifier,
  accessGroups,
  requirementValues,
  setRequirementValues,
  isMobile,
  invUsers,
  setInvUsers,
  selectedAccessGroups,
  setSelectedAccessGroups,
}: NewInvitationVisitDetailsProps) => {
  const labels = useAppSelector(selectLabels);

  const requirements =
    accessGroups[0]?.requirement_types?.filter((item) => item?.access_key) ??
    [];

  const textClass = (enabled: boolean) =>
    enabled ? "title-color" : "disabled-color";

  return (
    <div
      className="w-100 border-box hidden flex flex-dir-col align-items-center gap-6 p-8"
      style={{
        backgroundColor: "white",
        border: "1px #e0e0e0 solid",
        borderRadius: 6,
      }}
    >
      <div className="w-100">
        <p className="t-nowrap body-text-lg font-w-500 title-color mb-4">
          {labels.visit_details}
        </p>
      </div>
      <div className="w-100 border-box flex flex-dir-col align-items-start gap-10">
        <div className="w-100 flex flex-dir-col gap-6">
          <div>
            <p className="t-nowrap body-text-xs mb-3">{labels.motive} *</p>
            <TextField
              fullWidth
              placeholder={labels.reason_for_visit}
              value={motive}
              size="small"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setMotive(event.target.value);
              }}
            />
          </div>
          <div>
            <p className="t-nowrap body-text-xs mb-3">
              {`${labels.key_identifier} (${labels.optional})`}
            </p>
            <TextField
              fullWidth
              placeholder={labels.identifier_placeholder}
              value={identifier}
              size="small"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setIdentifier(event.target.value);
              }}
            />
          </div>
          {multipleAccess && setInvUsers && (
            <div className="w-100 border-box flex align-items-center justify-content-end">
              <p className="body-text-xs mr-6">
                {labels.allowed_access_tooltip}
              </p>
              <TextField
                type="number"
                style={{ width: 120 }}
                value={invUsers}
                size="small"
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setInvUsers(event.target.value);
                }}
              />
            </div>
          )}
          {requirements.length > 0 && (
            <div className="w-100 border-box flex flex-dir-col gap-6">
              {requirements.map((item, index) => (
                <RequirementsComponent
                  key={`${item?.id}-${index}`}
                  requirement={item}
                  requirementIndex={index}
                  value={requirementValues}
                  setValue={setRequirementValues}
                />
              ))}
            </div>
          )}
          <div>
            <p className="t-nowrap body-text-xs mb-3">
              {labels.allowed_access_points} {labels.and}{" "}
              {labels.restrictions_lowercase}
            </p>
            {accessGroups.map((item, index) => {
              const itemConfiguration = item?.configuration ?? [];
              const includedAccessGroups = selectedAccessGroups?.find(
                (group) =>
                  (group.configuration ?? [])[0]?.description ===
                  itemConfiguration[0]?.description,
              );
              const isEnabled = !!includedAccessGroups;

              return (
                <div
                  key={`${item?.id}-${index}`}
                  className={
                    accessGroups.length - 1 !== index
                      ? "flex flex-dir-col gap-4 mb-4"
                      : "flex flex-dir-col gap-4"
                  }
                >
                  {(item?.access_points ?? []).map((accPoint, accPointindex) => {
                    const accessPointConfigs = itemConfiguration.filter(
                      (config) => config?.id === accPoint?.id,
                    );

                    return (
                      <div
                        key={`${accPoint?.id}-${accPointindex}`}
                        className="border-box p-5"
                        style={{
                          border: "1px #e2e2e2 solid",
                          borderRadius: 8,
                          opacity: isEnabled ? 1 : 0.7,
                        }}
                      >
                        <div className="w-100 flex align-items-start justify-content-between mb-2">
                          <div>
                            <p
                              className={`body-text-sm font-w-500 mb-1 ${textClass(
                                isEnabled,
                              )}`}
                            >
                              {accPoint?.name}
                            </p>
                            <p
                              className={`body-text-xs ${textClass(isEnabled)}`}
                            >
                              {labels.location}: {accPoint?.vanue_name || "-"}
                            </p>
                          </div>
                          {accessGroups.length > 1 && selectedAccessGroups && (
                            <IconifyIcon
                              icon={
                                includedAccessGroups
                                  ? "ph:minus-fill"
                                  : "ph:plus-fill"
                              }
                              className={
                                includedAccessGroups &&
                                selectedAccessGroups.length < 2
                                  ? "disabled-color"
                                  : "primary-color"
                              }
                              style={{
                                fontSize: 20,
                                cursor:
                                  includedAccessGroups &&
                                  selectedAccessGroups.length < 2
                                    ? "default"
                                    : "pointer",
                              }}
                              onClick={
                                includedAccessGroups &&
                                selectedAccessGroups.length < 2
                                  ? () => {}
                                  : () => {
                                      setSelectedAccessGroups(
                                        includedAccessGroups
                                          ? selectedAccessGroups.filter(
                                              (group) => group.id !== item.id,
                                            )
                                          : [...selectedAccessGroups, item],
                                      );
                                    }
                              }
                            />
                          )}
                        </div>

                        {accessPointConfigs.map((configuration, configIndex) => {
                          const photos = configuration?.photos ?? [];
                          const showExit =
                            hasExitRestrictionSchedule(configuration);

                          return (
                            <div
                              key={`${configuration?.id}-${configIndex}`}
                              className="w-100"
                              style={{
                                borderTop:
                                  configIndex > 0 ? "1px solid #eee" : "none",
                                marginTop: configIndex > 0 ? 12 : 8,
                                paddingTop: configIndex > 0 ? 12 : 0,
                              }}
                            >
                              {configuration?.description && (
                                <p
                                  className={`body-text-sm font-w-500 mb-4 ${textClass(
                                    isEnabled,
                                  )}`}
                                >
                                  {configuration.description}
                                </p>
                              )}

                              <div className="w-100 flex flex-dir-col gap-4">
                                <div>
                                  <p
                                    className={`body-text-xs font-w-500 mb-1 ${textClass(
                                      isEnabled,
                                    )}`}
                                  >
                                    {labels.permitted_entry_schedule}
                                  </p>
                                  <p
                                    className={`body-text-sm ${textClass(
                                      isEnabled,
                                    )}`}
                                  >
                                    {formatAccessScheduleLabel(
                                      configuration?.start_time,
                                      configuration?.end_time,
                                      labels.all_day,
                                    )}
                                  </p>
                                </div>

                                {showExit && (
                                  <div>
                                    <p
                                      className={`body-text-xs font-w-500 mb-1 ${textClass(
                                        isEnabled,
                                      )}`}
                                    >
                                      {labels.permitted_exit_schedule}
                                    </p>
                                    <p
                                      className={`body-text-sm ${textClass(
                                        isEnabled,
                                      )}`}
                                    >
                                      {`${formatAccessScheduleTime(
                                        getExitRestrictionFrom(configuration),
                                      )} - ${formatAccessScheduleTime(
                                        getExitRestrictionTo(configuration),
                                      )}`}
                                    </p>
                                  </div>
                                )}

                                <div>
                                  <p
                                    className={`body-text-xs font-w-500 mb-1 ${textClass(
                                      isEnabled,
                                    )}`}
                                  >
                                    {labels.days_allowed}
                                  </p>
                                  <p
                                    className={`body-text-sm ${textClass(
                                      isEnabled,
                                    )}`}
                                  >
                                    {formatAllowedDaysLabel(
                                      configuration?.week_days ?? [],
                                      labels,
                                    )}
                                  </p>
                                </div>

                                {photos.length > 0 && (
                                  <div>
                                    <p
                                      className={`body-text-xs font-w-500 mb-2 ${textClass(
                                        isEnabled,
                                      )}`}
                                    >
                                      {labels.required_photographs}
                                    </p>
                                    <div className="flex flex-wrap align-items-center gap-3">
                                      {photos.map((photo, photoIndex) => (
                                        <div
                                          key={`${photo}-${photoIndex}`}
                                          className="flex align-items-center gap-2"
                                          style={{
                                            border: "1px solid #e6e6e6",
                                            borderRadius: 16,
                                            padding: "4px 10px",
                                          }}
                                        >
                                          <IconifyIcon
                                            icon={getAccessPhotoIcon(photo)}
                                            style={{
                                              fontSize: 14,
                                              color: isEnabled
                                                ? "#10418a"
                                                : "#9e9e9e",
                                            }}
                                          />
                                          <p
                                            className={`body-text-xs m-0 ${textClass(
                                              isEnabled,
                                            )}`}
                                          >
                                            {getAccessPhotoLabel(photo, labels)}
                                          </p>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewInvitationVisitDetails;
